import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import {

  CheckCircle2,

  ChevronRight,

  CreditCard,

  Loader2,

  MessageCircle,

  Ruler,

  Scissors,

  Send,

  ShoppingBag,

  Truck,

  X,

} from "lucide-react";

import { toast } from "react-toastify";

import {

  clearMadeToMeasureDraft,

  getMadeToMeasureDraft,

  getMyMadeToMeasureRequests,

  quoteMadeToMeasureConfiguration,

  resetMadeToMeasureDraftClear,

  resetMadeToMeasureDraftSubmit,

  resetMadeToMeasureQuote,

  selectMadeToMeasureDraftProduct,

  submitMadeToMeasureDraft,

  updateMadeToMeasureDraftContact,

  updateMadeToMeasureDraftFulfillment,

  updateMadeToMeasureDraftMeasurements,

  updateMadeToMeasureDraftStep,

} from "../../actions/madeToMeasureActions";

import Loader from "../../components/Loader";

import Message from "../../components/Message";

import Meta from "../../components/Meta";

import {

  selectMadeToMeasureDraft,

  selectMadeToMeasureDraftClear,

  selectMadeToMeasureDraftSubmit,

  selectMadeToMeasureDraftUpdate,

  selectMadeToMeasureMyRequests,

  selectMadeToMeasureQuote,

} from "../../selectors/madeToMeasureSelectors";

import { getSafeUserMessage } from "../../utils/errorMessages";

import {

  MTM_STEPS,

  MTM_STEP_LABELS,

  getMtmShopUrl,

  normalizeMtmStep,

} from "../../utils/madeToMeasureJourney";

import "./MadeToMeasureScreen.css";

const FALLBACK_IMAGE = "https://placehold.co/640x760?text=Academy+Costumes+London";

const JOURNEY_STEPS = [

  MTM_STEPS.GARMENT,

  MTM_STEPS.FABRIC,

  MTM_STEPS.FINISHING,

  MTM_STEPS.MEASUREMENTS,

  MTM_STEPS.FULFILLMENT,

  MTM_STEPS.REVIEW,

];

const MEASUREMENT_FIELDS = [

  ["waist", "Waist"],

  ["hip", "Hip"],

  ["bust", "Bust"],

  ["length", "Length"],

  ["shoulder", "Shoulder"],

  ["sleeve", "Sleeve"],

];

const EMPTY_MEASUREMENTS = {

  method: "self",

  unit: "cm",

  waist: "",

  hip: "",

  bust: "",

  length: "",

  shoulder: "",

  sleeve: "",

  notes: "",

};

const EMPTY_FULFILLMENT = {

  method: "pickup",

  storeKey: "",

  pickupNotes: "",

  instructions: "",

  deliveryAddress: {

    fullName: "",

    company: "",

    line1: "",

    line2: "",

    city: "",

    state: "",

    postalCode: "",

    country: "GB",

    phone: "",

  },

};

const EMPTY_CONTACT = {

  customerName: "",

  customerEmail: "",

  customerPhone: "",

};

const PRODUCTION_LABELS = Object.freeze({

  submitted: "Submitted",

  new: "Submitted",

  reviewing: "Reviewing",

  needs_details: "Details needed",

  quoted: "Quote prepared",

  quote_sent: "Quote sent",

  accepted: "Quote accepted",

  payment_pending: "Payment pending",

  paid: "Paid",

  in_production: "In production",

  cutting: "Cutting",

  fitting: "Fitting",

  finishing: "Finishing",

  ready_for_collection: "Ready for collection",

  dispatched: "Dispatched",

  completed: "Completed",

  rejected: "Closed",

  cancelled: "Cancelled",

});

function safeText(value = "") {

  return String(value ?? "").trim();

}

function normalizeLower(value = "") {

  return safeText(value).toLowerCase();

}

function isEmail(value = "") {

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeText(value));

}

function formatMoney(value = 0, currency = "GBP") {

  try {

    return new Intl.NumberFormat("en-GB", {

      style: "currency",

      currency: safeText(currency) || "GBP",

    }).format(Number(value || 0));

  } catch {

    return `£${Number(value || 0).toFixed(2)}`;

  }

}

function formatDate(value = "") {

  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {

    day: "2-digit",

    month: "short",

    year: "numeric",

  }).format(date);

}

function formatStatus(value = "") {

  const normalized = normalizeLower(value);

  if (PRODUCTION_LABELS[normalized]) return PRODUCTION_LABELS[normalized];

  return safeText(value || "submitted")

    .replace(/[_-]+/g, " ")

    .replace(/\b\w/g, (match) => match.toUpperCase());

}

function getRequestId(request = {}) {

  return safeText(request.id || request._id);

}

function getShortReference(item = {}) {

  const id = getRequestId(item);

  return id ? `MTM-${id.slice(-6).toUpperCase()}` : "MTM request";

}

function hasDraftSelection(selection = {}) {

  return Boolean(selection?.product || selection?.snapshot?.productId);

}

function getSelectionSnapshot(selection = {}) {

  return selection?.snapshot || {};

}

function getSelectionName(selection = {}, fallback = "Not selected") {

  const snapshot = getSelectionSnapshot(selection);

  return safeText(snapshot.name) || fallback;

}

function getSelectionImage(selection = {}) {

  const snapshot = getSelectionSnapshot(selection);

  return safeText(snapshot.image) || FALLBACK_IMAGE;

}

function getSelectionDetail(selection = {}) {

  const snapshot = getSelectionSnapshot(selection);

  const details = [

    snapshot.categoryName,

    snapshot.material,

    snapshot.unitSummary,

    snapshot.selectedOption?.label,

    snapshot.sku ? `SKU ${snapshot.sku}` : "",

  ].filter((value) => safeText(value));

  return details.slice(0, 3).join(" | ");

}

function getProductLink(selection = {}, fallbackStep = MTM_STEPS.GARMENT) {

  const snapshot = getSelectionSnapshot(selection);

  const shopKey = safeText(snapshot.shopKey);

  const slug = safeText(snapshot.slug);

  if (shopKey && slug) {

    return `/shop/${encodeURIComponent(shopKey)}/product/${encodeURIComponent(

      slug,

    )}?mtmStep=${encodeURIComponent(fallbackStep)}`;

  }

  return getMtmShopUrl(fallbackStep);

}

function getDraftSelection(draft = {}, step = "") {

  switch (normalizeMtmStep(step)) {

    case MTM_STEPS.GARMENT:

      return draft?.garment || null;

    case MTM_STEPS.FABRIC:

      return draft?.fabric || null;

    default:

      return null;

  }

}

function getFinishingSelections(draft = {}) {

  return Array.isArray(draft?.finishing) ? draft.finishing : [];

}

function hasMeasurementValues(measurements = {}) {

  return MEASUREMENT_FIELDS.some(([key]) => safeText(measurements[key]));

}

function isStepComplete(step = "", draft = {}) {

  const normalized = normalizeMtmStep(step);

  if (normalized === MTM_STEPS.GARMENT) {

    return hasDraftSelection(draft?.garment);

  }

  if (normalized === MTM_STEPS.FABRIC) {

    return hasDraftSelection(draft?.fabric);

  }

  if (normalized === MTM_STEPS.FINISHING) {

    return true;

  }

  if (normalized === MTM_STEPS.MEASUREMENTS) {

    const measurements = draft?.measurements || {};

    return (

      normalizeLower(measurements.method) === "appointment" ||

      hasMeasurementValues(measurements)

    );

  }

  if (normalized === MTM_STEPS.FULFILLMENT) {

    return Boolean(draft?.fulfillment?.method);

  }

  if (normalized === MTM_STEPS.REVIEW) {

    return hasDraftSelection(draft?.garment) && hasDraftSelection(draft?.fabric);

  }

  return false;

}

function getStepLockReason(step = "", draft = {}) {

  const normalized = normalizeMtmStep(step);

  if (normalized === MTM_STEPS.GARMENT) return "";

  if (!hasDraftSelection(draft?.garment)) {

    return "Choose a garment design to begin.";

  }

  if (

    [

      MTM_STEPS.FINISHING,

      MTM_STEPS.MEASUREMENTS,

      MTM_STEPS.FULFILLMENT,

      MTM_STEPS.REVIEW,

    ].includes(normalized) &&

    !hasDraftSelection(draft?.fabric)

  ) {

    return "Choose your fabric before continuing.";

  }

  return "";

}

function getNextStep(step = "") {

  const index = JOURNEY_STEPS.indexOf(normalizeMtmStep(step));

  return JOURNEY_STEPS[Math.min(index + 1, JOURNEY_STEPS.length - 1)];

}

function getPreviousStep(step = "") {

  const index = JOURNEY_STEPS.indexOf(normalizeMtmStep(step));

  return JOURNEY_STEPS[Math.max(index - 1, 0)];

}

function getDraftEstimate(draft = {}) {

  return draft?.estimate || {};

}

function getDraftProductId(selection = {}) {

  return safeText(selection?.product || selection?.snapshot?.productId);

}

function getDraftSelectedOption(selection = {}) {

  return selection?.snapshot?.selectedOption || {};

}

function buildQuotePayloadFromDraft(draft = {}) {

  const finishing = getFinishingSelections(draft);

  return {

    designId: getDraftProductId(draft?.garment),

    designOption: getDraftSelectedOption(draft?.garment),

    fabricId: getDraftProductId(draft?.fabric),

    fabricQuantity: 1,

    fabricOption: getDraftSelectedOption(draft?.fabric),

    finishing: finishing.map((item) => ({

      productId: safeText(item.productId || item.product),

      quantity: Number(item.quantity || 1),

      selectedOption: item.snapshot?.selectedOption || {},

    })),

    deliveryMethodId: draft?.fulfillment?.method || "",

  };

}

function getQuoteBlockMessage(quoteState = {}, draft = {}) {

  const hasSelection =

    hasDraftSelection(draft?.garment) ||

    hasDraftSelection(draft?.fabric) ||

    getFinishingSelections(draft).length > 0;

  if (!hasSelection) return "";

  if (quoteState.loading) return "Updating your estimate…";

  if (quoteState.error) return quoteState.error;

  if (quoteState.purchasable === false) {

    const issue = Array.isArray(quoteState.issues) ? quoteState.issues[0] : null;

    return safeText(issue?.message) || "One or more selections is unavailable.";

  }

  return "";

}

function formatRetryCountdown(seconds = 0) {

  const safeSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));

  if (safeSeconds >= 60) {

    const minutes = Math.ceil(safeSeconds / 60);

    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

  }

  return `${safeSeconds} ${safeSeconds === 1 ? "second" : "seconds"}`;

}

function buildRetryAwareError(state = {}, remainingSeconds = 0) {

  const message = safeText(state?.error);

  const retry = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));

  if (!message) return "";

  if (!retry) return message;

  return `${message} You can try again in ${formatRetryCountdown(retry)}.`;

}

function useRetryCountdown(retryAfterSeconds = 0, resetKey = "") {

  const [remaining, setRemaining] = useState(0);

  useEffect(() => {

    const initial = Math.max(0, Math.ceil(Number(retryAfterSeconds) || 0));

    setRemaining(initial);

    if (!initial) return undefined;

    const timer = setInterval(() => {

      setRemaining((current) => Math.max(0, current - 1));

    }, 1000);

    return () => clearInterval(timer);

  }, [retryAfterSeconds, resetKey]);

  return remaining;

}

function getRequestProductName(request = {}) {

  return (

    safeText(request.productSnapshot?.name) ||

    safeText(request.selectedProduct?.name) ||

    "Made to measure garment"

  );

}

function getRequestFabricName(request = {}) {

  return (

    safeText(request.textileSnapshot?.name) ||

    safeText(request.selectedTextile?.name) ||

    "Textile to be confirmed"

  );

}

function getLatestTimeline(request = {}) {

  const timeline = Array.isArray(request.timeline) ? request.timeline : [];

  return timeline[timeline.length - 1] || null;

}

function getActionUrl(payload = {}) {

  return (

    safeText(payload.checkoutUrl) ||

    safeText(payload.hostedCheckoutUrl) ||

    safeText(payload.approvalUrl) ||

    safeText(payload.approveUrl) ||

    safeText(payload.payerActionUrl) ||

    safeText(payload.nextAction?.url)

  );

}

function normalizeMeasurementsForm(measurements = {}) {

  return {

    ...EMPTY_MEASUREMENTS,

    ...measurements,

    method: normalizeLower(measurements.method) === "appointment" ? "appointment" : "self",

    unit: normalizeLower(measurements.unit) === "inches" ? "inches" : "cm",

  };

}

function normalizeFulfillmentForm(fulfillment = {}) {

  return {

    ...EMPTY_FULFILLMENT,

    ...fulfillment,

    method: normalizeLower(fulfillment.method) === "delivery" ? "delivery" : "pickup",

    deliveryAddress: {

      ...EMPTY_FULFILLMENT.deliveryAddress,

      ...(fulfillment.deliveryAddress || {}),

    },

  };

}

function normalizeContactForm(contact = {}) {

  return {

    ...EMPTY_CONTACT,

    ...contact,

  };

}

function Field({ label, children, className = "" }) {

  return (

    <label className={`mtm-field ${className}`.trim()}>

      <span>{label}</span>

      {children}

    </label>

  );

}

function StepNav({ activeStep, draft, onSelect }) {
  const activeIndex = JOURNEY_STEPS.indexOf(activeStep);
  const stepNumber = Math.max(1, activeIndex + 1);

  return (
    <section className="mtm-journey" id="mtm-journey" aria-label="Your made-to-measure journey">
      <div className="mtm-journey__intro">
        <div>
          <span className="mtm-journey__eyebrow">Your made-to-measure journey</span>
          <h2>Six steps to your perfect fit</h2>
          <p>
            Choose a design, select your fabric, personalise the details and tell
            our studio how you would like your garment fitted and delivered.
          </p>
        </div>
        <span className="mtm-journey__progress" aria-live="polite">
          Step {stepNumber} of {JOURNEY_STEPS.length}
        </span>
      </div>
      <nav className="mtm-steps" aria-label="Made-to-measure ordering steps">
        {JOURNEY_STEPS.map((step, index) => {
          const active = step === activeStep;
          const complete = isStepComplete(step, draft);
          const lockReason = getStepLockReason(step, draft);
          return (
            <button
              key={step}
              type="button"
              className={`mtm-step${active ? " is-active" : ""}${complete ? " is-complete" : ""}`}
              disabled={Boolean(lockReason)}
              title={lockReason || MTM_STEP_LABELS[step]}
              aria-current={active ? "step" : undefined}
              onClick={() => onSelect(step)}
            >
              <span aria-hidden="true">{index + 1}</span>
              <strong>{MTM_STEP_LABELS[step]}</strong>
            </button>
          );
        })}
      </nav>
    </section>
  );
}

function SelectionSnapshot({

  step,

  selection,

  title,

  emptyText,

  actionLabel = "",

  onAction = null,

}) {

  const selected = hasDraftSelection(selection);

  if (!selected) {

    return (

      <div className="mtm-selection mtm-selection--empty">

        <div>

          <strong>{title}</strong>

          <p>{emptyText}</p>

        </div>

      </div>

    );

  }

  const price = getSelectionSnapshot(selection).priceFrom || getSelectionSnapshot(selection).price;

  return (

    <article className="mtm-selection">

      <img

        src={getSelectionImage(selection)}

        alt={getSelectionName(selection, title)}

        onError={(event) => {

          event.currentTarget.src = FALLBACK_IMAGE;

        }}

      />

      <div>

        <span>{title}</span>

        <strong>{getSelectionName(selection)}</strong>

        {getSelectionDetail(selection) ? <p>{getSelectionDetail(selection)}</p> : null}

        {price ? <small>{formatMoney(price)}</small> : null}

      </div>

      <div className="mtm-selection__actions">

        <Link to={getProductLink(selection, step)}>View</Link>

        {actionLabel && typeof onAction === "function" ? (

          <button

            type="button"

            className="mtm-link-button"

            onClick={onAction}

          >

            {actionLabel}

          </button>

        ) : null}

      </div>

    </article>

  );

}

function SelectionStep({

  step,

  title,

  eyebrow,

  summary,

  selection,

  lockedReason = "",

  actionLabel = "Browse products",

  children,

}) {

  const shopUrl = getMtmShopUrl(step);

  return (

    <section className="mtm-panel">

      <div className="mtm-panel__head">

        <span>{eyebrow}</span>

        <h2>{title}</h2>

        <p>{summary}</p>

      </div>

      {lockedReason ? <Message variant="info">{lockedReason}</Message> : null}

      <SelectionSnapshot

        step={step}

        selection={selection}

        title={MTM_STEP_LABELS[step]}

        emptyText="Nothing selected yet. Browse the collection to find the right option."

      />

      <div className="mtm-action-row">

        {lockedReason ? (

          <button type="button" className="mtm-button" disabled>

            {actionLabel}

          </button>

        ) : (

          <Link className="mtm-button mtm-button--primary" to={shopUrl}>

            <ShoppingBag size={16} />

            {actionLabel}

          </Link>

        )}

        {children}

      </div>

    </section>

  );

}

function FinishingStep({

  draft,

  updateState,

  updateRetryRemaining = 0,

  onClear,

  onRemove,

  onContinue,

}) {

  const finishing = getFinishingSelections(draft);

  const lockedReason = getStepLockReason(MTM_STEPS.FINISHING, draft);

  const updatePaused = updateRetryRemaining > 0;

  return (

    <section className="mtm-panel">

      <div className="mtm-panel__head">

        <span>Step 3</span>

        <h2>Optional finishing</h2>

        <p>Personalise your garment with buttons, fastenings or trims. These finishing touches are optional.</p>

      </div>

      {lockedReason ? <Message variant="info">{lockedReason}</Message> : null}

      {finishing.length ? (

        <div className="mtm-finishing-list">

          {finishing.map((item) => {

            const productId = safeText(item.productId || item.product);

            const selection = { product: productId, snapshot: item.snapshot };

            return (

              <article className="mtm-finishing-item" key={productId}>

                <img

                  src={getSelectionImage(selection)}

                  alt={getSelectionName(selection, "Finishing item")}

                  onError={(event) => {

                    event.currentTarget.src = FALLBACK_IMAGE;

                  }}

                />

                <div>

                  <strong>{getSelectionName(selection)}</strong>

                  <p>{getSelectionDetail(selection) || "Haberdashery item"}</p>

                  <small>Quantity {Number(item.quantity || 1)}</small>

                </div>

                <button

                  type="button"

                  className="mtm-icon-button"

                  disabled={updateState.loading || updatePaused}

                  onClick={() => onRemove(productId)}

                  aria-label={`Remove ${getSelectionName(selection)}`}

                >

                  <X size={16} />

                </button>

              </article>

            );

          })}

        </div>

      ) : (

        <div className="mtm-selection mtm-selection--empty">

          <div>

            <strong>No finishing touches added yet</strong>

            <p>Keep your design simple or browse our haberdashery for finishing details.</p>

          </div>

        </div>

      )}

      <div className="mtm-action-row">

        {lockedReason ? (

          <button type="button" className="mtm-button" disabled>

            Browse haberdashery

          </button>

        ) : (

          <Link className="mtm-button mtm-button--primary" to={getMtmShopUrl(MTM_STEPS.FINISHING)}>

            <Scissors size={16} />

            Browse haberdashery

          </Link>

        )}

        <button

          type="button"

          className="mtm-button"

          disabled={!finishing.length || updateState.loading || updatePaused}

          onClick={onClear}

        >

          Clear finishing

        </button>

        <button

          type="button"

          className="mtm-button"

          disabled={Boolean(lockedReason) || updatePaused}

          onClick={onContinue}

        >

          Continue to measurements

        </button>

      </div>

    </section>

  );

}

function MeasurementsStep({

  form,

  updateState,

  updateRetryRemaining = 0,

  onChange,

  onSave,

  onContinue,

}) {

  const updatePaused = updateRetryRemaining > 0;

  return (

    <section className="mtm-panel">

      <div className="mtm-panel__head">

        <span>Step 4</span>

        <h2>Measurements</h2>

        <p>Share your measurements or request an in-store fitting. Our studio will review the details before making your garment.</p>

      </div>

      <div className="mtm-choice-row" role="group" aria-label="Measurement method">

        <button

          type="button"

          className={form.method === "self" ? "is-active" : ""}

          onClick={() => onChange({ method: "self" })}

        >

          <Ruler size={16} />

          I will provide measurements

        </button>

        <button

          type="button"

          className={form.method === "appointment" ? "is-active" : ""}

          onClick={() => onChange({ method: "appointment" })}

        >

          <MessageCircle size={16} />

          In-store fitting appointment

        </button>

      </div>

      <div className="mtm-form-grid">

        <Field label="Unit">

          <select

            value={form.unit}

            onChange={(event) => onChange({ unit: event.target.value })}

          >

            <option value="cm">Centimetres</option>

            <option value="inches">Inches</option>

          </select>

        </Field>

        {MEASUREMENT_FIELDS.map(([key, label]) => (

          <Field label={label} key={key}>

            <input

              type="text"

              inputMode="decimal"

              value={form[key] || ""}

              onChange={(event) => onChange({ [key]: event.target.value })}

              placeholder={form.unit === "inches" ? "e.g. 28" : "e.g. 72"}

            />

          </Field>

        ))}

        <Field label="Measurement notes" className="mtm-field--wide">

          <textarea

            rows={4}

            value={form.notes || ""}

            onChange={(event) => onChange({ notes: event.target.value })}

            placeholder="Tell us about your preferred fit, comfort and any details our tailors should consider."

          />

        </Field>

      </div>

      {updateState.error ? <Message variant="danger">{updateState.error}</Message> : null}

      <div className="mtm-step-actions">

        <button

          type="button"

          className="mtm-button"

          disabled={updateState.loading || updatePaused}

          onClick={onSave}

        >

          {updateState.loading ? <Loader2 size={16} className="mtm-spin" /> : <CheckCircle2 size={16} />}

          Save measurements

        </button>

        <button

          type="button"

          className="mtm-button mtm-button--primary"

          disabled={updateState.loading || updatePaused}

          onClick={onContinue}

        >

          Continue

          <ChevronRight size={16} />

        </button>

      </div>

    </section>

  );

}

function FulfillmentStep({

  form,

  updateState,

  updateRetryRemaining = 0,

  onChange,

  onAddressChange,

  onSave,

  onContinue,

}) {

  const delivery = form.method === "delivery";

  const updatePaused = updateRetryRemaining > 0;

  return (

    <section className="mtm-panel">

      <div className="mtm-panel__head">

        <span>Step 5</span>

        <h2>Delivery or collection</h2>

        <p>Tell us whether you would prefer to collect your finished garment or have it delivered.</p>

      </div>

      <div className="mtm-choice-row" role="group" aria-label="Fulfillment method">

        <button

          type="button"

          className={!delivery ? "is-active" : ""}

          onClick={() => onChange({ method: "pickup" })}

        >

          <ShoppingBag size={16} />

          Collection

        </button>

        <button

          type="button"

          className={delivery ? "is-active" : ""}

          onClick={() => onChange({ method: "delivery" })}

        >

          <Truck size={16} />

          Delivery

        </button>

      </div>

      {!delivery ? (

        <div className="mtm-form-grid">

          <Field label="Collection location">

            <input

              type="text"

              value={form.storeKey || ""}

              onChange={(event) => onChange({ storeKey: event.target.value })}

              placeholder="Academy Costumes London"

            />

          </Field>

          <Field label="Collection notes" className="mtm-field--wide">

            <textarea

              rows={4}

              value={form.pickupNotes || ""}

              onChange={(event) => onChange({ pickupNotes: event.target.value })}

              placeholder="Tell us your preferred collection days or any access requirements."

            />

          </Field>

        </div>

      ) : (

        <div className="mtm-form-grid">

          <Field label="Full name">

            <input

              type="text"

              value={form.deliveryAddress.fullName || ""}

              onChange={(event) => onAddressChange({ fullName: event.target.value })}

            />

          </Field>

          <Field label="Phone">

            <input

              type="tel"

              value={form.deliveryAddress.phone || ""}

              onChange={(event) => onAddressChange({ phone: event.target.value })}

            />

          </Field>

          <Field label="Address line 1" className="mtm-field--wide">

            <input

              type="text"

              value={form.deliveryAddress.line1 || ""}

              onChange={(event) => onAddressChange({ line1: event.target.value })}

            />

          </Field>

          <Field label="Address line 2" className="mtm-field--wide">

            <input

              type="text"

              value={form.deliveryAddress.line2 || ""}

              onChange={(event) => onAddressChange({ line2: event.target.value })}

            />

          </Field>

          <Field label="City">

            <input

              type="text"

              value={form.deliveryAddress.city || ""}

              onChange={(event) => onAddressChange({ city: event.target.value })}

            />

          </Field>

          <Field label="Postcode">

            <input

              type="text"

              value={form.deliveryAddress.postalCode || ""}

              onChange={(event) => onAddressChange({ postalCode: event.target.value })}

            />

          </Field>

          <Field label="Country">

            <input

              type="text"

              maxLength={2}

              value={form.deliveryAddress.country || "GB"}

              onChange={(event) => onAddressChange({ country: event.target.value.toUpperCase() })}

            />

          </Field>

          <Field label="Delivery instructions" className="mtm-field--wide">

            <textarea

              rows={4}

              value={form.instructions || ""}

              onChange={(event) => onChange({ instructions: event.target.value })}

              placeholder="Add delivery instructions or any information our studio should know."

            />

          </Field>

        </div>

      )}

      {updateState.error ? <Message variant="danger">{updateState.error}</Message> : null}

      <div className="mtm-step-actions">

        <button

          type="button"

          className="mtm-button"

          disabled={updateState.loading || updatePaused}

          onClick={onSave}

        >

          {updateState.loading ? <Loader2 size={16} className="mtm-spin" /> : <CheckCircle2 size={16} />}

          Save preference

        </button>

        <button

          type="button"

          className="mtm-button mtm-button--primary"

          disabled={updateState.loading || updatePaused}

          onClick={onContinue}

        >

          Continue

          <ChevronRight size={16} />

        </button>

      </div>

    </section>

  );

}

function ReviewStep({

  draft,

  contactForm,

  notes,

  quoteState,

  submitState,

  updateState,

  submitRetryRemaining = 0,

  updateRetryRemaining = 0,

  missingItems,

  onContactChange,

  onNotesChange,

  onSaveContact,

  onSubmit,

}) {

  const estimate = getDraftEstimate(draft);

  const quote = quoteState?.quote || {};

  const updatePaused = updateRetryRemaining > 0;

  const submitPaused = submitRetryRemaining > 0;

  const estimatedTotal =

    quoteState?.success && quote.estimatedTotal !== undefined

      ? quote.estimatedTotal

      : estimate.estimatedTotal || 0;

  return (

    <section className="mtm-panel">

      <div className="mtm-panel__head">

        <span>Step 6</span>

        <h2>Review and submit</h2>

        <p>Check your choices and contact details. Our studio will review your request and confirm the final price before payment.</p>

      </div>

      <div className="mtm-review-grid">

        <SelectionSnapshot

          step={MTM_STEPS.GARMENT}

          selection={draft?.garment}

          title="Design"

          emptyText="Choose a ready-made design."

        />

        <SelectionSnapshot

          step={MTM_STEPS.FABRIC}

          selection={draft?.fabric}

          title="Fabric"

          emptyText="Choose a textile."

        />

      </div>

      <div className="mtm-total">

        <span>Your draft estimate</span>

        <strong>{formatMoney(estimatedTotal)}</strong>

        <small>This is an estimate, not a final price. Our studio will confirm your quote before payment.</small>

      </div>

      <div className="mtm-form-grid">

        <Field label="Name">

          <input

            type="text"

            value={contactForm.customerName || ""}

            onChange={(event) => onContactChange({ customerName: event.target.value })}

          />

        </Field>

        <Field label="Email">

          <input

            type="email"

            value={contactForm.customerEmail || ""}

            onChange={(event) => onContactChange({ customerEmail: event.target.value })}

          />

        </Field>

        <Field label="Phone">

          <input

            type="tel"

            value={contactForm.customerPhone || ""}

            onChange={(event) => onContactChange({ customerPhone: event.target.value })}

          />

        </Field>

        <Field label="Project notes" className="mtm-field--wide">

          <textarea

            rows={5}

            value={notes}

            onChange={(event) => onNotesChange(event.target.value)}

            placeholder="Share your preferred fit, occasion, deadline or any special requirements."

          />

        </Field>

      </div>

      {missingItems.length ? (

        <Message variant="warning">{missingItems[0]}</Message>

      ) : null}

      {updateState.error ? <Message variant="danger">{updateState.error}</Message> : null}

      {submitState.error ? <Message variant="danger">{submitState.error}</Message> : null}

      <div className="mtm-step-actions">

        <button

          type="button"

          className="mtm-button"

          disabled={updateState.loading || updatePaused}

          onClick={onSaveContact}

        >

          {updateState.loading ? <Loader2 size={16} className="mtm-spin" /> : <CheckCircle2 size={16} />}

          Save contact

        </button>

        <button

          type="button"

          className="mtm-button mtm-button--primary"

          disabled={

            Boolean(missingItems.length) ||

            submitState.loading ||

            updateState.loading ||

            submitPaused ||

            updatePaused

          }

          onClick={onSubmit}

        >

          {submitState.loading ? <Loader2 size={16} className="mtm-spin" /> : <Send size={16} />}

          Send request for studio review

        </button>

      </div>

    </section>

  );

}

function DraftSummary({

  draft,

  quoteState,

  onChangeStep,

  onStartOver,

  clearing = false,

}) {

  const estimate = getDraftEstimate(draft);

  const quote = quoteState?.quote || {};

  const finishing = getFinishingSelections(draft);

  const measurements = draft?.measurements || {};

  const fulfillment = draft?.fulfillment || {};

  const quoteMessage = getQuoteBlockMessage(quoteState, draft);

  const estimatedTotal =

    quoteState?.success && quote.estimatedTotal !== undefined

      ? quote.estimatedTotal

      : estimate.estimatedTotal || 0;

  return (

    <aside className="mtm-summary" aria-label="Made to measure draft summary">

      <div className="mtm-summary__head">

        <div>

          <span>Draft</span>

          <h2>{getSelectionName(draft?.garment, "Made to measure")}</h2>

        </div>

        <button

          type="button"

          className="mtm-link-button mtm-link-button--danger"

          disabled={clearing}

          onClick={onStartOver}

        >

          {clearing ? "Clearing..." : "Start over"}

        </button>

      </div>

      <SelectionSnapshot

        step={MTM_STEPS.GARMENT}

        selection={draft?.garment}

        title="Design"

        emptyText="No design selected."

        actionLabel="Change"

        onAction={() => onChangeStep?.(MTM_STEPS.GARMENT)}

      />

      <SelectionSnapshot

        step={MTM_STEPS.FABRIC}

        selection={draft?.fabric}

        title="Fabric"

        emptyText="No fabric selected."

        actionLabel="Change"

        onAction={() => onChangeStep?.(MTM_STEPS.FABRIC)}

      />

      <div className="mtm-summary__line">

        <span>Finishing</span>

        <strong>{finishing.length ? `${finishing.length} selected` : "Optional"}</strong>

        <button

          type="button"

          className="mtm-link-button"

          onClick={() => onChangeStep?.(MTM_STEPS.FINISHING)}

        >

          Change

        </button>

      </div>

      <div className="mtm-summary__line">

        <span>Measurements</span>

        <strong>

          {normalizeLower(measurements.method) === "appointment"

            ? "In-store fitting appointment"

            : hasMeasurementValues(measurements)

              ? "Saved"

              : "Not added"}

        </strong>

        <button

          type="button"

          className="mtm-link-button"

          onClick={() => onChangeStep?.(MTM_STEPS.MEASUREMENTS)}

        >

          Change

        </button>

      </div>

      <div className="mtm-summary__line">

        <span>Fulfillment</span>

        <strong>{formatStatus(fulfillment.method || "pickup")}</strong>

        <button

          type="button"

          className="mtm-link-button"

          onClick={() => onChangeStep?.(MTM_STEPS.FULFILLMENT)}

        >

          Change

        </button>

      </div>

      <div className="mtm-summary__total">

        <span>Current estimate</span>

        <strong>{formatMoney(estimatedTotal)}</strong>

        {quoteState?.loading ? <small>Updating your estimate…</small> : null}

        {quoteMessage && !quoteState?.loading ? <small>{quoteMessage}</small> : null}

      </div>

    </aside>

  );

}

function ClearDraftDialog({

  open,

  loading = false,

  error = "",

  onCancel,

  onConfirm,

}) {

  if (!open) return null;

  return (

    <div

      className="mtm-dialog-backdrop"

      role="presentation"

      onMouseDown={(event) => {

        if (event.target === event.currentTarget && !loading) {

          onCancel?.();

        }

      }}

    >

      <section

        className="mtm-dialog"

        role="dialog"

        aria-modal="true"

        aria-labelledby="mtm-clear-draft-title"

      >

        <div>

          <h2 id="mtm-clear-draft-title">Start a new request?</h2>

          <p>

            This will remove the garment, fabric, finishing selections and

            information entered for this made-to-measure request. Your account

            and saved measurement profiles will not be deleted.

          </p>

        </div>

        {error ? <Message variant="danger">{error}</Message> : null}

        <div className="mtm-dialog__actions">

          <button

            type="button"

            className="mtm-button"

            disabled={loading}

            onClick={onCancel}

          >

            Keep this request

          </button>

          <button

            type="button"

            className="mtm-button mtm-button--danger"

            disabled={loading}

            onClick={onConfirm}

          >

            {loading ? <Loader2 size={16} className="mtm-spin" /> : null}

            Start a new request

          </button>

        </div>

      </section>

    </div>

  );

}

function ProductionProgress({ request }) {

  const progress = request?.productionProgress || {};

  const steps = Array.isArray(progress.steps) ? progress.steps : [];

  const stepIndex = Number(progress.stepIndex ?? -1);

  const percent = Math.max(0, Math.min(100, Number(progress.percent || 0)));

  if (!steps.length || stepIndex < 0) {

    return (

      <div className="mtm-production">

        <span>Quote progress</span>

        <strong>{formatStatus(request.status)}</strong>

      </div>

    );

  }

  return (

    <div className="mtm-production">

      <div>

        <span>Production progress</span>

        <strong>{formatStatus(progress.status || request.status)}</strong>

      </div>

      <div className="mtm-production__bar" aria-hidden="true">

        <span style={{ width: `${percent}%` }} />

      </div>

      <ol>

        {steps.map((step, index) => (

          <li key={step} className={index <= stepIndex ? "is-complete" : ""}>

            {formatStatus(step)}

          </li>

        ))}

      </ol>

    </div>

  );

}

function MadeToMeasureRequestCard({ request, label = "" }) {

  const requestId = getRequestId(request);

  const detailUrl = requestId ? `/made-to-measure/${encodeURIComponent(requestId)}/pay` : "/made-to-measure";

  const orderNumber = safeText(request.linkedOrder?.orderNumber || request.orderNumber);

  const latestTimeline = getLatestTimeline(request);

  const quote = Number(request.finalQuotePrice || 0);

  return (

    <article className="mtm-request-card">

      <div className="mtm-request-card__head">

        <div>

          <span>{label || getShortReference(request)}</span>

          <h3>{getRequestProductName(request)}</h3>

          <p>{getRequestFabricName(request)}</p>

        </div>

        <strong>{formatStatus(request.status)}</strong>

      </div>

      <ProductionProgress request={request} />

      <dl className="mtm-request-card__meta">

        <div>

          <dt>Quote</dt>

          <dd>{quote > 0 ? formatMoney(quote) : "Pending"}</dd>

        </div>

        <div>

          <dt>Payment</dt>

          <dd>{request.isPaid ? "Paid" : request.canPay ? "Ready" : "Not ready"}</dd>

        </div>

        <div>

          <dt>Updated</dt>

          <dd>{formatDate(latestTimeline?.createdAt || request.updatedAt) || "Pending"}</dd>

        </div>

      </dl>

      {latestTimeline?.message ? <p>{latestTimeline.message}</p> : null}

      {request.paymentBlockedReason && !request.canPay && !request.isPaid ? (

        <p>{request.paymentBlockedReason}</p>

      ) : null}

      <div className="mtm-request-card__actions">

        <Link className="mtm-button" to={detailUrl}>

          <MessageCircle size={16} />

          View updates

        </Link>

        {request.canPay && !request.isPaid ? (

          <Link className="mtm-button mtm-button--primary" to={detailUrl}>

            <CreditCard size={16} />

            Accept and pay

          </Link>

        ) : null}

        {orderNumber ? (

          <Link className="mtm-button" to={`/order/${encodeURIComponent(orderNumber)}`}>

            View order

          </Link>

        ) : null}

      </div>

    </article>

  );

}

function RequestsDashboard({ requestsState }) {

  const previousRequests = Array.isArray(requestsState.previousRequests)

    ? requestsState.previousRequests

    : [];

  return (

    <section className="mtm-dashboard" id="mtm-requests" aria-label="Made to measure requests">

      <div className="mtm-dashboard__head">

        <div>

          <span>Your requests</span>

          <h2>Made To Measure Dashboard</h2>

        </div>

        <a className="mtm-button" href="#made-to-measure-draft">

          Current draft

        </a>

      </div>

      {requestsState.loading ? (

        <div className="mtm-state mtm-state--compact">

          <Loader2 className="mtm-spin" />

          <p>Loading your requests...</p>

        </div>

      ) : requestsState.error ? (

        <Message variant="danger">{requestsState.error}</Message>

      ) : (

        <>

          {requestsState.currentRequest ? (

            <div className="mtm-dashboard__group">

              <h3>Active request</h3>

              <MadeToMeasureRequestCard

                request={requestsState.currentRequest}

                label={getShortReference(requestsState.currentRequest)}

              />

            </div>

          ) : null}

          <div className="mtm-dashboard__group">

            <h3>Previous requests</h3>

            {previousRequests.length ? (

              <div className="mtm-request-grid">

                {previousRequests.slice(0, 4).map((request) => (

                  <MadeToMeasureRequestCard

                    key={getRequestId(request)}

                    request={request}

                    label={getShortReference(request)}

                  />

                ))}

              </div>

            ) : (

              <p className="mtm-empty">Submitted requests will appear here.</p>

            )}

          </div>

        </>

      )}

    </section>

  );

}

export default function MadeToMeasureScreen() {

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const draftState = useSelector(selectMadeToMeasureDraft);

  const updateState = useSelector(selectMadeToMeasureDraftUpdate);

  const submitState = useSelector(selectMadeToMeasureDraftSubmit);

  const clearState = useSelector(selectMadeToMeasureDraftClear);

  const quoteState = useSelector(selectMadeToMeasureQuote);

  const myRequests = useSelector(selectMadeToMeasureMyRequests);

  const userAuth = useSelector((state) => state.userAuth || {});

  const {

    checked = true,

    isAuthenticated = false,

    userInfo = null,

  } = userAuth;

  const userId = safeText(userInfo?.id || userInfo?._id);

  const isLoggedIn = Boolean(checked && isAuthenticated && userId);

  const draft = draftState.draft || null;

  const urlStep = normalizeMtmStep(searchParams.get("step"));

  const requestedStep =

    urlStep || normalizeMtmStep(draft?.currentStep) || MTM_STEPS.GARMENT;

  const activeStep =

    draft && getStepLockReason(requestedStep, draft)

      ? hasDraftSelection(draft.garment)

        ? MTM_STEPS.FABRIC

        : MTM_STEPS.GARMENT

      : requestedStep;

  const [measurementsForm, setMeasurementsForm] = useState(EMPTY_MEASUREMENTS);

  const [fulfillmentForm, setFulfillmentForm] = useState(EMPTY_FULFILLMENT);

  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);

  const [notes, setNotes] = useState("");

  const [stateMessageShown, setStateMessageShown] = useState(false);

  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const updateRetryRemaining = useRetryCountdown(

    updateState.retryAfterSeconds,

    `${updateState.requestId}:${updateState.status}:${updateState.error}`,

  );

  const submitRetryRemaining = useRetryCountdown(

    submitState.retryAfterSeconds,

    `${submitState.requestId}:${submitState.status}:${submitState.error}`,

  );

  const updateNoticeState = useMemo(

    () => ({

      ...updateState,

      error: buildRetryAwareError(updateState, updateRetryRemaining),

    }),

    [updateRetryRemaining, updateState],

  );

  const submitNoticeState = useMemo(

    () => ({

      ...submitState,

      error: buildRetryAwareError(submitState, submitRetryRemaining),

    }),

    [submitRetryRemaining, submitState],

  );

  const quotePayload = useMemo(

    () => (draft ? buildQuotePayloadFromDraft(draft) : null),

    [draft],

  );

  const quotePayloadKey = useMemo(

    () => JSON.stringify(quotePayload || {}),

    [quotePayload],

  );

  useEffect(() => {

    const payload = JSON.parse(quotePayloadKey || "{}");

    if (!isLoggedIn) {

      dispatch(resetMadeToMeasureQuote());

      return undefined;

    }

    const hasQuoteableSelection =

      Boolean(payload.designId) ||

      Boolean(payload.fabricId) ||

      (Array.isArray(payload.finishing) && payload.finishing.length > 0);

    if (!hasQuoteableSelection) {

      dispatch(resetMadeToMeasureQuote());

      return undefined;

    }

    const controller = new AbortController();

    const timer = setTimeout(() => {

      dispatch(

        quoteMadeToMeasureConfiguration(payload, {

          signal: controller.signal,

        }),

      ).catch(() => undefined);

    }, 250);

    return () => {

      clearTimeout(timer);

      controller.abort();

    };

  }, [dispatch, isLoggedIn, quotePayloadKey]);

  useEffect(() => {

    if (!checked) return;

    if (!isLoggedIn) return;

    dispatch(getMadeToMeasureDraft()).catch((error) => {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not load your made-to-measure draft. Please try again later.",

        ),

      );

    });

    dispatch(getMyMadeToMeasureRequests()).catch(() => undefined);

  }, [checked, dispatch, isLoggedIn, userId]);

  useEffect(() => {

    return () => {

      dispatch(resetMadeToMeasureDraftSubmit());

      dispatch(resetMadeToMeasureDraftClear());

    };

  }, [dispatch]);

  useEffect(() => {

    if (stateMessageShown || !location.state?.message) return;

    toast.success(safeText(location.state.message));

    setStateMessageShown(true);

  }, [location.state, stateMessageShown]);

  useEffect(() => {

    if (!draft?.id && !draft?._id) return;

    setMeasurementsForm(normalizeMeasurementsForm(draft.measurements || {}));

    setFulfillmentForm(normalizeFulfillmentForm(draft.fulfillment || {}));

    setContactForm(normalizeContactForm(draft.contact || {}));

    setNotes(safeText(draft.notes));

  }, [

    draft?.id,

    draft?._id,

    draft?.updatedAt,

    draft?.measurements,

    draft?.fulfillment,

    draft?.contact,

    draft?.notes,

  ]);

  useEffect(() => {

    if (!isLoggedIn || urlStep || !draft?.currentStep) return;

    const next = new URLSearchParams(searchParams);

    next.set("step", normalizeMtmStep(draft.currentStep) || MTM_STEPS.GARMENT);

    setSearchParams(next, { replace: true });

  }, [draft?.currentStep, isLoggedIn, searchParams, setSearchParams, urlStep]);

  const goToStep = useCallback(

    (step) => {

      const normalized = normalizeMtmStep(step) || MTM_STEPS.GARMENT;

      const lockReason = getStepLockReason(normalized, draft || {});

      if (lockReason) {

        toast.info(lockReason);

        return;

      }

      const next = new URLSearchParams(searchParams);

      next.set("step", normalized);

      setSearchParams(next, { replace: false });

      const savedStep = normalizeMtmStep(draft?.currentStep);

      if (isLoggedIn && updateRetryRemaining <= 0 && savedStep !== normalized) {

        dispatch(updateMadeToMeasureDraftStep(normalized)).catch(() => undefined);

      }

    },

    [

      dispatch,

      draft,

      isLoggedIn,

      searchParams,

      setSearchParams,

      updateRetryRemaining,

    ],

  );

  const openClearDraftDialog = useCallback(() => {

    dispatch(resetMadeToMeasureDraftClear());

    setClearDialogOpen(true);

  }, [dispatch]);

  const closeClearDraftDialog = useCallback(() => {

    if (clearState.loading) return;

    setClearDialogOpen(false);

    dispatch(resetMadeToMeasureDraftClear());

  }, [clearState.loading, dispatch]);

  const handleClearDraft = useCallback(async () => {

    try {

      const result = await dispatch(clearMadeToMeasureDraft(draft?.id || draft?._id));

      setMeasurementsForm(EMPTY_MEASUREMENTS);

      setFulfillmentForm(EMPTY_FULFILLMENT);

      setContactForm(normalizeContactForm(result?.draft?.contact || {}));

      setNotes("");

      dispatch(resetMadeToMeasureQuote());

      const next = new URLSearchParams(searchParams);

      next.set("step", MTM_STEPS.GARMENT);

      setSearchParams(next, { replace: true });

      setClearDialogOpen(false);

      toast.success(result?.message || "Your draft has been cleared.");

    } catch (error) {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not clear your made-to-measure draft. Please try again later.",

        ),

      );

    }

  }, [dispatch, draft, searchParams, setSearchParams]);

  const saveMeasurements = useCallback(async () => {

    try {

      const result = await dispatch(updateMadeToMeasureDraftMeasurements(measurementsForm));

      toast.success(result?.message || "Measurements saved.");

      goToStep(MTM_STEPS.FULFILLMENT);

    } catch (error) {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not save your measurements. Please try again later.",

        ),

      );

    }

  }, [dispatch, goToStep, measurementsForm]);

  const saveFulfillment = useCallback(async () => {

    try {

      const result = await dispatch(updateMadeToMeasureDraftFulfillment(fulfillmentForm));

      toast.success(result?.message || "Delivery preference saved.");

      goToStep(MTM_STEPS.REVIEW);

    } catch (error) {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not save your delivery preference. Please try again later.",

        ),

      );

    }

  }, [dispatch, fulfillmentForm, goToStep]);

  const saveContact = useCallback(async () => {

    const result = await dispatch(

      updateMadeToMeasureDraftContact({

        ...contactForm,

        notes,

      }),

    );

    return result;

  }, [contactForm, dispatch, notes]);

  const handleSaveContact = useCallback(async () => {

    try {

      const result = await saveContact();

      toast.success(result?.message || "Contact details saved.");

    } catch (error) {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not save your contact details. Please try again later.",

        ),

      );

    }

  }, [saveContact]);

  const clearFinishing = useCallback(async () => {

    try {

      const result = await dispatch(

        selectMadeToMeasureDraftProduct(MTM_STEPS.FINISHING, { clear: true }),

      );

      toast.success(result?.message || "Finishing selections cleared.");

    } catch (error) {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not update finishing selections. Please try again later.",

        ),

      );

    }

  }, [dispatch]);

  const removeFinishing = useCallback(

    async (productId) => {

      try {

        const result = await dispatch(

          selectMadeToMeasureDraftProduct(MTM_STEPS.FINISHING, {

            productId,

            remove: true,

          }),

        );

        toast.success(result?.message || "Finishing item removed.");

      } catch (error) {

        toast.error(

          getSafeUserMessage(

            error?.message,

            "We could not remove this finishing item. Please try again later.",

          ),

        );

      }

    },

    [dispatch],

  );

  const missingItems = useMemo(() => {

    const missing = [];

    if (!hasDraftSelection(draft?.garment)) {

      missing.push("Choose a ready-made design before submitting.");

    }

    if (!hasDraftSelection(draft?.fabric)) {

      missing.push("Choose a textile before submitting.");

    }

    if (

      measurementsForm.method !== "appointment" &&

      !hasMeasurementValues(measurementsForm)

    ) {

      missing.push("Add your measurements or choose in-store measurement.");

    }

    if (!safeText(contactForm.customerName)) {

      missing.push("Add your name before submitting.");

    }

    if (!isEmail(contactForm.customerEmail)) {

      missing.push("Add a valid email address before submitting.");

    }

    if (

      fulfillmentForm.method === "delivery" &&

      !safeText(fulfillmentForm.deliveryAddress.line1)

    ) {

      missing.push("Add a delivery address before submitting.");

    }

    if (quoteState.purchasable === false) {

      missing.push(

        getQuoteBlockMessage(quoteState, draft) ||

          "One or more selected items is no longer available.",

      );

    }

    if (quoteState.error) {

      missing.push(

        "Confirm current pricing and availability before submitting.",

      );

    }

    return missing;

  }, [contactForm, draft, fulfillmentForm, measurementsForm, quoteState]);

  const handleSubmit = useCallback(async () => {

    if (missingItems.length) {

      toast.info(missingItems[0]);

      return;

    }

    try {

      await dispatch(updateMadeToMeasureDraftMeasurements(measurementsForm));

      await dispatch(updateMadeToMeasureDraftFulfillment(fulfillmentForm));

      await saveContact();

      const result = await dispatch(submitMadeToMeasureDraft());

      toast.success(result?.message || "Your made-to-measure request has been submitted.");

      dispatch(getMyMadeToMeasureRequests()).catch(() => undefined);

      const actionUrl = getActionUrl(result);

      if (actionUrl) {

        window.location.assign(actionUrl);

        return;

      }

      navigate("/made-to-measure?step=review", { replace: true });

    } catch (error) {

      toast.error(

        getSafeUserMessage(

          error?.message,

          "We could not submit your made-to-measure request. Please try again later.",

        ),

      );

    }

  }, [

    dispatch,

    fulfillmentForm,

    measurementsForm,

    missingItems,

    navigate,

    saveContact,

  ]);

  const submittedRequest = submitState.enquiry;

  const renderActiveStep = () => {

    if (!draft) return null;

    if (activeStep === MTM_STEPS.FABRIC) {

      return (

        <SelectionStep

          step={MTM_STEPS.FABRIC}

          eyebrow="Step 2"

          title="Choose a textile"

          summary="Browse the Textiles shop in selection mode, choose the cloth for this garment, and return here for finishing."

          selection={getDraftSelection(draft, MTM_STEPS.FABRIC)}

          lockedReason={getStepLockReason(MTM_STEPS.FABRIC, draft)}

          actionLabel="Browse textiles"

        />

      );

    }

    if (activeStep === MTM_STEPS.FINISHING) {

      return (

        <FinishingStep

          draft={draft}

          updateState={updateNoticeState}

          updateRetryRemaining={updateRetryRemaining}

          onClear={clearFinishing}

          onRemove={removeFinishing}

          onContinue={() => goToStep(MTM_STEPS.MEASUREMENTS)}

        />

      );

    }

    if (activeStep === MTM_STEPS.MEASUREMENTS) {

      return (

        <MeasurementsStep

          form={measurementsForm}

          updateState={updateNoticeState}

          updateRetryRemaining={updateRetryRemaining}

          onChange={(patch) =>

            setMeasurementsForm((current) => ({ ...current, ...patch }))

          }

          onSave={saveMeasurements}

          onContinue={saveMeasurements}

        />

      );

    }

    if (activeStep === MTM_STEPS.FULFILLMENT) {

      return (

        <FulfillmentStep

          form={fulfillmentForm}

          updateState={updateNoticeState}

          updateRetryRemaining={updateRetryRemaining}

          onChange={(patch) =>

            setFulfillmentForm((current) => ({ ...current, ...patch }))

          }

          onAddressChange={(patch) =>

            setFulfillmentForm((current) => ({

              ...current,

              deliveryAddress: {

                ...current.deliveryAddress,

                ...patch,

              },

            }))

          }

          onSave={saveFulfillment}

          onContinue={saveFulfillment}

        />

      );

    }

    if (activeStep === MTM_STEPS.REVIEW) {

      return (

        <ReviewStep

          draft={draft}

          contactForm={contactForm}

          notes={notes}

          quoteState={quoteState}

          submitState={submitNoticeState}

          updateState={updateNoticeState}

          submitRetryRemaining={submitRetryRemaining}

          updateRetryRemaining={updateRetryRemaining}

          missingItems={missingItems}

          onContactChange={(patch) =>

            setContactForm((current) => ({ ...current, ...patch }))

          }

          onNotesChange={setNotes}

          onSaveContact={handleSaveContact}

          onSubmit={handleSubmit}

        />

      );

    }

    return (

      <SelectionStep

        step={MTM_STEPS.GARMENT}

        eyebrow="Step 1"

        title="Begin with a Ready-Made silhouette"

        summary="Browse the Ready-Made shop in selection mode, choose the garment that sets the silhouette, and return here to continue with fabric."

        selection={getDraftSelection(draft, MTM_STEPS.GARMENT)}

        actionLabel="Browse Ready-Made designs"

      />

    );

  };

  if (!checked) {

    return (

      <main className="mtm-page mtm-page--state">

        <Loader />

      </main>

    );

  }

  if (!isLoggedIn) {

    const redirect = encodeURIComponent("/made-to-measure");

    return (

      <main className="mtm-page">

        <Meta

          title="Made To Measure | Academy Costumes London"

          description="Build a made-to-measure request from Academy Costumes London products."

        />

        <section className="mtm-login">

          <span>Made To Measure</span>

          <h1>Start your private fitting draft</h1>

          <p>

            Sign in to save selections, measurements, delivery preferences, quote

            updates, payment status, and production progress to your account.

          </p>

          <div className="mtm-action-row">

            <Link className="mtm-button mtm-button--primary" to={`/login?redirect=${redirect}`}>

              Sign in

            </Link>

            <Link className="mtm-button" to={`/register?redirect=${redirect}`}>

              Create account

            </Link>

          </div>

        </section>

      </main>

    );

  }

  return (

    <main className="mtm-page">

      <Meta

        title="Made To Measure | Academy Costumes London"

        description="Build a made-to-measure request from Academy Costumes London products."

      />

      <section className="mtm-hero" id="made-to-measure-draft" aria-labelledby="mtm-hero-title">
        <div className="mtm-hero__content">
          <span className="mtm-hero__eyebrow">ACADEMY COSTUMES LONDON</span>
          <h1 id="mtm-hero-title">Made to Measure. <em>Made for You.</em></h1>
          <p className="mtm-hero__lead">
            A garment that starts with your vision and is shaped around you.
          </p>
          <p className="mtm-hero__description">
            Choose your design, fabric and finishing touches. Share your measurements
            and delivery preferences, and our London studio will review your
            request and confirm the final quote before payment.
          </p>
          <div className="mtm-hero__actions">
            <a className="mtm-button mtm-button--primary" href="#mtm-journey">
              Begin your journey <ChevronRight size={18} />
            </a>
            <a className="mtm-button mtm-hero__secondary" href="#mtm-requests">
              View my requests
            </a>
          </div>
          <div className="mtm-hero__reassurance" aria-label="How the process works">
            <span>Personal design choices</span>
            <span>Made-to-measure fitting</span>
            <span>Studio-confirmed quote</span>
          </div>
        </div>
     




        <div className="mtm-hero__visual">
  <img
    src="/images/madeToMeasure/made-to-measure-hero.jpg"
    alt="Made-to-measure tailoring at Academy Costumes London"
    className="mtm-hero__image"
  />

  <div className="mtm-hero__visual-overlay">
    <span>THE ART OF THE PERFECT FIT</span>
    <strong>
      Crafted around <em>your vision.</em>
    </strong>
  </div>
</div>
      </section>

      <div className="mtm-shell">

        <RequestsDashboard requestsState={myRequests} />

        {submitState.success && submittedRequest ? (

          <section className="mtm-success" aria-live="polite">

            <CheckCircle2 size={22} />

            <div>

              <strong>{submitState.message || "Your request has been submitted."}</strong>

              <p>Reference {getShortReference(submittedRequest)}</p>

              {submitState.customerUpdateUrl ? (

                <a className="mtm-button mtm-button--primary" href={submitState.customerUpdateUrl}>

                  View updates

                </a>

              ) : null}

            </div>

          </section>

        ) : null}

        {draftState.loading && !draft ? (

          <section className="mtm-state">

            <Loader2 className="mtm-spin" />

            <p>Loading your made-to-measure draft...</p>

          </section>

        ) : draftState.error ? (

          <section className="mtm-state">

            <Message variant="danger">{draftState.error}</Message>

            <button

              type="button"

              className="mtm-button"

              onClick={() => dispatch(getMadeToMeasureDraft())}

            >

              Try again

            </button>

          </section>

        ) : draft ? (

          <>

            <StepNav activeStep={activeStep} draft={draft} onSelect={goToStep} />

            <div className="mtm-layout">

              <div className="mtm-main">

                {renderActiveStep()}

                <div className="mtm-nav-row">

                  <button

                    type="button"

                    className="mtm-button"

                    disabled={activeStep === MTM_STEPS.GARMENT}

                    onClick={() => goToStep(getPreviousStep(activeStep))}

                  >

                    Back

                  </button>

                  {activeStep !== MTM_STEPS.REVIEW ? (

                    <button

                      type="button"

                      className="mtm-button mtm-button--primary"

                      disabled={Boolean(getStepLockReason(getNextStep(activeStep), draft))}

                      onClick={() => goToStep(getNextStep(activeStep))}

                    >

                      Next

                      <ChevronRight size={16} />

                    </button>

                  ) : null}

                </div>

              </div>

              <DraftSummary

                draft={draft}

                quoteState={quoteState}

                onChangeStep={goToStep}

                onStartOver={openClearDraftDialog}

                clearing={clearState.loading}

              />

            </div>

            <ClearDraftDialog

              open={clearDialogOpen}

              loading={clearState.loading}

              error={clearState.error}

              onCancel={closeClearDraftDialog}

              onConfirm={handleClearDraft}

            />

          </>

        ) : null}

      </div>

    </main>

  );

}
